import { AlertService } from '@anecdotes/design-system/alert';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Observable, Subject, firstValueFrom, map } from 'rxjs';
import { FileDownloadingHelperService } from '@anecdotes/helpers';
import { TrustCenterFacadeService, TrustCenterService, openFileUpload } from '@anecdotes/shared-trust-center';
import { AbstractControl, ValidatorFn, Validators } from '@angular/forms';
import { CustomValidators } from '@anecdotes/validators';

const PDF_FORMATS = '.pdf';

@Component({
  selector: 'app-legal-settings',
  templateUrl: './legal-settings.component.html',
  styleUrls: ['./legal-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LegalSettingsComponent implements OnInit {
  readonly downloadingNda$ = new Subject<boolean>();
  readonly uploadingNda$ = new Subject<boolean>();

  readonly submitNdaEmailsCallback = this.submitNdaEmails.bind(this);
  readonly submitTosUrlCallback = this.submitTosUrl.bind(this);
  readonly submitPrivacyUrlCallback = this.submitPrivacyUrl.bind(this);

  readonly emailValidators: ValidatorFn[] = [
    Validators.required,
    (control: AbstractControl) => CustomValidators.emailCustomValidator(control, true),
  ];
  readonly termsOfServiceValidators: ValidatorFn[] = [CustomValidators.url(true)];
  readonly privacyPolicyValidators: ValidatorFn[] = [Validators.required, CustomValidators.url(true)];

  readonly emailErrors: Record<string, string> = {
    email: this.buildTranslationKey('settingErrors.email'),
  };
  readonly urlErrors: Record<string, string> = {
    url: this.buildTranslationKey('settingErrors.url'),
  };

  ndaFileName$: Observable<string>;
  emailsToSendNdas$: Observable<string>;
  tosUrl$: Observable<string>;
  privacyPolicyUrl$: Observable<string>;
  hasWatermark: boolean;

  constructor(
    private fileDownloadingHelper: FileDownloadingHelperService,
    private trustCenterFacadeService: TrustCenterFacadeService,
    private trustCenterService: TrustCenterService,
    private alertService: AlertService,
    private cd: ChangeDetectorRef,
  ) {}

  async ngOnInit(): Promise<void> {
    const settings$ = this.trustCenterFacadeService.getSettings();
    this.ndaFileName$ = settings$.pipe(map((settings) => settings?.nda_filename));
    this.emailsToSendNdas$ = settings$.pipe(map((settings) => settings?.nda_emails));

    const tcInfo$ = this.trustCenterFacadeService.getTrustCenterInfo();
    this.tosUrl$ = tcInfo$.pipe(map((tcInfo) => tcInfo?.tos));
    this.privacyPolicyUrl$ = tcInfo$.pipe(map((tcInfo) => tcInfo?.privacy_policy));

    this.hasWatermark = await firstValueFrom(this.trustCenterFacadeService.hasFilesWatermark());
    this.cd.detectChanges();
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.${key}`;
  }

  openNdaUpload(): void {
    openFileUpload(this.handleNdaFileSelected.bind(this), PDF_FORMATS);
  }

  async handleNdaFileSelected(file: File): Promise<void> {
    this.uploadingNda$.next(true);

    try {
      await this.trustCenterFacadeService.uploadNda(file);
    } catch {
      this.alertService.openActionErrorAlert({
        customBaseTranslationKey: this.buildTranslationKey('ndaUploadErrorToast'),
      });
    } finally {
      this.uploadingNda$.next(false);
    }
  }

  async downloadNda(): Promise<void> {
    try {
      this.downloadingNda$.next(true);
      const file = await firstValueFrom(this.trustCenterService.downloadNda());
      this.fileDownloadingHelper.downloadFile(file);
    } finally {
      this.downloadingNda$.next(false);
    }
  }

  async submitNdaEmails(value: string): Promise<void> {
    await this.trustCenterFacadeService.updateSettings({
      nda_emails: value,
    });
  }

  async submitTosUrl(value: string): Promise<void> {
    await this.trustCenterFacadeService.updateTrustCenterInfo({
      tos: value.trim(),
    });
  }

  async submitPrivacyUrl(value: string): Promise<void> {
    await this.trustCenterFacadeService.updateTrustCenterInfo({
      privacy_policy: value.trim(),
    });
  }

  async updateWatermarkFlag(hasWatermark: boolean): Promise<void> {
    const previousValue = this.hasWatermark;
    this.hasWatermark = hasWatermark;
    this.cd.detectChanges();

    try {
      await this.trustCenterFacadeService.updateSettings({ watermark: hasWatermark });
    } catch {
      this.hasWatermark = previousValue;
      this.cd.detectChanges();
    }
  }
}
